import { useCallback, useRef } from 'react';

import { CACHE_WRITE_ID_KEY, type FullContext } from '../types';

export type CanWriteToCacheFn = ({ cache }: Pick<FullContext, 'cache'>) => Promise<boolean>;

/**
 * Allows to check if we can actually write to the cache.
 * Reads a test value, increments it, writes it, reads it back
 * and confirms that it's been updated.
 *
 * Needed because some people might have cookies blocked, or at capacity.
 *
 * Returns a function that, when called, will do the cache check
 * and memoize the result.
 */
export const useCanWriteToCache = (): CanWriteToCacheFn => {
	const canWriteRef = useRef<Promise<boolean> | null>(null);

	return useCallback(async ({ cache }) => {
		if (canWriteRef.current === null) {
			canWriteRef.current = canWriteToCache({ cache });
		}
		return await canWriteRef.current;
	}, []);
};

/**
 * This implementation does retries.
 *
 * Needed because the browser control library's async `setCookie()` function
 * has a bug/limitation when it doesn't correctly wait for the product context
 * to init. If we call the function "too early", it says the cookie is blocked
 * and doesn't do the write, even though once the product context loads,
 * the write would succeed.
 */
const canWriteToCache = async ({
	maxRetries = 2,
	retry = 0,
	retryDelay = 1000,
	cache,
}: {
	maxRetries?: number;
	retry?: number;
	retryDelay?: number;
} & Pick<FullContext, 'cache'>): Promise<boolean> => {
	try {
		const initialRead = cache.read({ ssr: false });
		const writtenId = (initialRead?.[CACHE_WRITE_ID_KEY] ?? 0) + 1;
		const writeResult = await cache.write({ ...initialRead, [CACHE_WRITE_ID_KEY]: writtenId });
		const wroteSuccessfully =
			writeResult === true && cache.read({ ssr: false })?.[CACHE_WRITE_ID_KEY] === writtenId;

		if (!wroteSuccessfully && retry < maxRetries) {
			await new Promise((resolve) => setTimeout(resolve, retryDelay));
			return await canWriteToCache({ retry: retry + 1, maxRetries, retryDelay, cache });
		}

		return wroteSuccessfully;
	} catch {
		return false;
	}
};
